import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import Select from "../forms/Select/Select";
import { defaultSelectStyles } from "../forms/Select/styles";
import { Button } from "../forms/Buttons/Button";
import { moveQuestionsToFolder } from "../../services/questionService";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { UserContext } from "../../utils/contexts/UserContext";
import { canCreateIn, subjectFolderOptions } from "../../utils/folders";
import { BookletLinks, bookletLockMessage, lockingBooklets, opensBooklets } from "../../pages/QuestionPage/review/bookletLock";

// How many codes of the questions a booklet keeps are named; the others are counted ("and 3 more").
const CODES_SHOWN = 8;

// The select is 44px tall on phones.
const selectStyles = {
  ...defaultSelectStyles,
  control: (provided, state) => ({ ...defaultSelectStyles.control(provided, state), "@media (max-width: 639px)": { minHeight: "2.75rem" } }),
};

// Moves `questionIds` to an exam's subject or to "No folder" (`allowNoFolder`) — POST move_to_folder, all or
// nothing; the subject comes from the folder. Offered subjects: where the role writes questions (a moderator
// moves any, an author their own from "No folder"); the server has the last word (move_not_allowed, with the
// questions it refused). A subject is named with its exam ("БТС-1 · Mathematics"): exams share subject names.
// An active BTS booklet keeps the questions it prints in their folder (409 question_in_booklet, nothing moved, with
// `questions`: those of the ones asked): the dialog names their codes (`questionCodes`, uuid -> code of the
// selection, a Map or an object) for as long as they are selected, whatever target is picked next, and, when others
// were asked too, its button moves only those, taking the booklet's ones out of the page's selection first
// (`onDeselect(uuids)`). Both are optional. When every selected question is kept, only "Close" is left.
export default function MoveToFolderDialog({
  isOpen,
  questionIds,
  folders,
  currentFolder,
  allowNoFolder = true,
  onClose,
  onMoved,
  questionCodes,
  onDeselect,
}) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  const [target, setTarget] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // The target an active BTS booklet refused (409 question_in_booklet) without naming the questions (an older
  // backend): the same move is not offered again, another target may be (a question already there does not move).
  const [refusedTarget, setRefusedTarget] = useState(null);
  // The questions an active BTS booklet keeps in their folder, as the 409s named them: the next move leaves them out.
  const [kept, setKept] = useState([]);
  // The last refusal that named them ({uuids, booklets, linked}). It stays when another target is picked: the
  // booklet keeps them from any folder.
  const [lock, setLock] = useState(null);
  // The main button is off while the move is sent, which drops the focus: after a refusal it goes to the footer's
  // last button (the main one, or "Close").
  const footerRef = useRef(null);
  const refocusRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setTarget(null);
      setError(null);
      setRefusedTarget(null);
      setKept([]);
      setLock(null);
      refocusRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (saving || !isOpen || !refocusRef.current) return;
    refocusRef.current = false;
    // Lost: on the page, or on a control that is off or gone (the select is turned off when nothing can move).
    const active = document.activeElement;
    if (active && active !== document.body && !active.disabled && active.isConnected) return;
    const buttons = footerRef.current?.querySelectorAll("button:not([disabled])");
    buttons?.[buttons.length - 1]?.focus();
  }, [saving, isOpen]);

  const targetOptions = useMemo(() => {
    const groups = subjectFolderOptions(folders, (s) => canCreateIn(s.role)).map((group) => ({
      ...group,
      options: group.options.map((o) => ({ ...o, label: `${group.label} · ${o.label}`, isDisabled: o.value === currentFolder })),
    }));
    return allowNoFolder
      ? [{ value: "null", label: t("folders.no_folder"), isDisabled: currentFolder === "null" }, ...groups]
      : groups;
  }, [folders, currentFolder, allowNoFolder, t]);

  // The questions that can still move; while some of those asked are kept, the button moves these only.
  const rest = useMemo(() => questionIds.filter((id) => !kept.includes(id)), [questionIds, kept]);
  const skipping = rest.length > 0 && rest.length < questionIds.length;
  // Every selected question is kept: nothing can move anywhere, so the dialog only says why.
  const stuck = rest.length === 0 && kept.length > 0;
  const canSubmit = !!target && !saving && rest.length > 0 && (kept.length > 0 || target !== refusedTarget);
  const lockShown = !!lock && lock.uuids.some((id) => questionIds.includes(id));

  const codeOf = (uuid) => (questionCodes instanceof Map ? questionCodes.get(uuid) : questionCodes?.[uuid]) || "";

  // The refusal naming the kept questions by their codes ("QA-1, QA-2 and 3 more"), then who returns the booklets to
  // draft (bookletLockMessage); without a known code, the general refusal. It says "booklet" or "booklets" by their
  // count, and names them only for those who do not get them as links right after.
  const keptMessage = ({ uuids, booklets, linked }) => {
    const known = uuids
      .map(codeOf)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    if (!known.length) {
      return bookletLockMessage(t, uuids.length > 1 ? "folders.move.in_booklet" : "folders.move.in_booklet_one", booklets, linked);
    }
    const shown = known.slice(0, CODES_SHOWN);
    const more = uuids.length - shown.length;
    const codes = more > 0 ? `${shown.join(", ")} ${t("folders.move.codes_more", { count: more })}` : shown.join(", ");
    const links = linked && booklets.some((booklet) => booklet.uuid);
    const key = `folders.move.${uuids.length > 1 ? "in_booklet_codes" : "in_booklet_code"}${links ? "_linked" : ""}`;
    return bookletLockMessage((k, options) => t(k, k === key ? { ...options, codes } : options), key, booklets, linked, {
      counted: true,
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const asked = skipping ? rest : questionIds;
    if (skipping) onDeselect?.(questionIds.filter((id) => kept.includes(id)));
    refocusRef.current = true;
    setSaving(true);
    setError(null);
    try {
      onMoved(await moveQuestionsToFolder({ questions: asked, folder: target === "null" ? null : target }));
    } catch (err) {
      const code = getApiErrorCode(err);
      const refused = err?.response?.data?.questions;
      if (code === "question_in_booklet") {
        // An active BTS booklet prints some of them: nothing moved. Said with the way out (an admin gets the
        // booklets' links); the questions it names are left out of the next move.
        const booklets = lockingBooklets(err);
        const linked = opensBooklets(user);
        const named = Array.isArray(refused) ? refused.filter((id) => asked.includes(id)) : [];
        if (named.length) {
          setKept((prev) => [...prev, ...named.filter((id) => !prev.includes(id))]);
          setLock({ uuids: named, booklets, linked });
        } else {
          setError({
            message: bookletLockMessage(t, asked.length > 1 ? "folders.move.in_booklet" : "folders.move.in_booklet_one", booklets, linked),
            booklets: linked ? booklets : [],
          });
          setRefusedTarget(target);
        }
        return;
      }
      const message = getApiErrorMessage(t, err, "folders.move.failed");
      setError({
        message:
          ["move_not_allowed", "exam_folder", "subject_mismatch"].includes(code) && Array.isArray(refused) && refused.length
            ? `${message} ${t("folders.move.not_allowed_count", { count: refused.length })}`
            : message,
      });
    } finally {
      setSaving(false);
    }
  };

  const hasTargets = targetOptions.some((o) => !o.options || o.options.length > 0);
  const close = () => !saving && onClose();

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={t("folders.move.title")} maxWidth="max-w-lg">
      {/* Esc closes the dialog from the select too, while its list is closed (react-select keeps that Esc). */}
      <div
        onKeyDownCapture={(e) => {
          if (e.key === "Escape" && e.target.getAttribute?.("aria-expanded") === "false" && !e.target.value) close();
        }}
      >
        <p className="text-sm text-slate-500">{t("folders.move.summary", { count: questionIds.length })}</p>
        <div className="mt-4 space-y-4">
          {hasTargets ? (
            <Select
              label={t("folders.subject_folder")}
              placeholder={t("folders.subject_folder_placeholder")}
              options={targetOptions}
              value={target}
              onChange={(o) => {
                setTarget(o?.value ?? null);
                setError(null);
              }}
              isSearchable
              isDisabled={stuck}
              customStyles={selectStyles}
            />
          ) : (
            <p className="text-sm text-slate-500">{t("folders.move.no_targets")}</p>
          )}

          {lockShown && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {keptMessage(lock)}
              {lock.linked && <BookletLinks booklets={lock.booklets} />}
            </p>
          )}
          {error && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {error.message}
              {error.booklets && <BookletLinks booklets={error.booklets} />}
            </p>
          )}
        </div>
        {/* Phones: the two buttons full width, one under the other (the main one may read "Move the other 3"). */}
        <div ref={footerRef} className="mt-6 flex flex-wrap justify-end gap-2 max-sm:flex-col max-sm:items-stretch">
          <Button
            type="button"
            color="secondary-outline"
            text={stuck ? t("buttons.close") : t("buttons.cancel")}
            onClick={onClose}
            disabled={saving}
            className="max-sm:h-11"
          />
          {!stuck && (
            <Button
              type="button"
              color="primary"
              text={
                !skipping
                  ? t("folders.move.submit")
                  : rest.length === 1
                  ? t("folders.move.submit_rest_single")
                  : t("folders.move.submit_rest", { count: rest.length })
              }
              onClick={handleSubmit}
              loading={saving}
              disabled={!canSubmit}
              className="max-sm:h-11"
            />
          )}
        </div>
      </div>
    </CustomDialog>
  );
}
